import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  InputAdornment,
  Tooltip,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Switch,
  FormControlLabel,
  Collapse,
  Checkbox,
} from '@mui/material';
import VerificationBadge from '../components/VerificationBadge';
import FinancialTrustBadge from '../components/FinancialTrustBadge';
import { useAuth } from '../hooks/useAuth';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Mail as MailIcon,
  Send as SendIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
  PersonAdd as PersonAddIcon,
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Security as SecurityIcon,
  ContactPage as IdCardIcon,
  Sync as SyncIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { Contact, UserSearchResult, UserDeliverySettings, ContactSearchResponse } from '../types';
import { getEmailDomainSync } from '../services/systemConfigService';
import api from '../services/api';
import {
  refreshContactFromApi,
  resolveContactId,
  seedContactIdentityCache,
  seedNewContactAfterAdd,
  setContactInboundRate,
  setContactNickname,
} from '../services/contactIdentityCache';
import {
  getLocalIdentityNotFoundMap,
  getLocalLookupMap,
  getLocalOutboundPostage,
  getLocalOutboundPostageMap,
  mergeContactsIntoLocalStore,
  readContactLocalStore,
  removeContactFromLocalStore,
  upsertContactInLocalStore,
} from '../services/contactLocalStore';
import { extractAlias } from '../utils/qixitAddress';
import identityDisclosureService, {
  type IdentityDisclosureShieldSnapshot,
  resolveApprovedShieldSnapshot,
} from '../services/identityDisclosureService';
import { formatMailingAddressForDisplay } from '../utils/mailingAddressFormat';
import ProfileOverlayDialog from '../components/profile/ProfileOverlayDialog';
import socialService, { ProfileSummaryDto } from '../services/socialService';
import { getProfileViewDeniedMessage, getSearchResultProfileTooltip, formatContactSearchLocation } from '../utils/profileViewAccess';
import {
  getContactSearchValidationMessage,
  isContactSearchResponse,
  meetsContactSearchMinimum,
} from '../utils/contactSearchRules';

const AddressBook: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const formatDate = (value?: string): string | null => {
    if (!value) return null;
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return null;
    return dt.toLocaleDateString();
  };

  const getIdVerifiedChipTooltip = (user: UserSearchResult): string => {
    const firstDate = formatDate(user.firstIdVerifiedDate);
    const latestDate = formatDate(user.latestIdVerifiedDate);
    const joinedDate = formatDate(user.joinedQixitDate);
    return [
      'Didit government-issued photo ID verification',
      latestDate ? `Most recent verification: ${latestDate}` : null,
      firstDate && firstDate !== latestDate ? `First verification: ${firstDate}` : null,
      joinedDate ? `Joined Qixit: ${joinedDate}` : null
    ].filter(Boolean).join('\n');
  };

  const isIdVerifiedNameChip = (user: UserSearchResult): boolean =>
    Boolean(user.isIdVerified && user.isDisplayNameIdVerifiedName);

  const isTraceableChip = (user: UserSearchResult): boolean =>
    Boolean(user.isIdVerified && !user.isDisplayNameIdVerifiedName);

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [fraudDialogOpen, setFraudDialogOpen] = useState(false);
  const [fraudTarget, setFraudTarget] = useState<Contact | null>(null);
  const [fraudSubmitting, setFraudSubmitting] = useState(false);
  const [fraudSelfConfirm, setFraudSelfConfirm] = useState(false);
  const [autoApproveAllRequests, setAutoApproveAllRequests] = useState(true);
  const [autoApproveSaving, setAutoApproveSaving] = useState(false);
  const [shieldDialogOpen, setShieldDialogOpen] = useState(false);
  const [shieldError, setShieldError] = useState<string | null>(null);
  const [shieldTarget, setShieldTarget] = useState<Contact | null>(null);
  const [shieldSnapshot, setShieldSnapshot] = useState<IdentityDisclosureShieldSnapshot | null>(null);
  const [shieldRequestMailingAddress, setShieldRequestMailingAddress] = useState(false);
  const [shieldCheckingUsername, setShieldCheckingUsername] = useState<string | null>(null);
  const [approvedShieldByUsername, setApprovedShieldByUsername] = useState<Record<string, boolean>>({});
  const [fraudForm, setFraudForm] = useState({
    requestIdentityPhotos: true,
    requestMailingAddress: false,
  });

  // Default delivery settings (expanded to match legacy system)
  const [deliverySettings, setDeliverySettings] = useState<UserDeliverySettings>({
    defaultDeliveryFeeRequired: 0,
    defaultMaxPayAmount: 0,
    defaultDaysUntilMailExpires: 90,
    maxDailyLimit: 1000,
    isUnlisted: false,
    defaultCommercialDeliveryFee: 0,
    doNotForwardSMTP: false,
    sendNewClientRequiredMessage: false,
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsEdited, setSettingsEdited] = useState(false);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [submittedSearchTerm, setSubmittedSearchTerm] = useState('');
  const [searchCountry, setSearchCountry] = useState('');
  const [searchState, setSearchState] = useState('');
  const [searchCity, setSearchCity] = useState('');
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);
  const [searchStatus, setSearchStatus] = useState<ContactSearchResponse['status'] | null>(null);
  const [searchMessage, setSearchMessage] = useState<string | null>(null);
  const [searchRefinement, setSearchRefinement] = useState<ContactSearchResponse['refinement']>(undefined);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserSearchResult | null>(null);
  const [contactLookupByAddress, setContactLookupByAddress] = useState<Record<string, UserSearchResult>>({});
  const [identityNotFoundByContact, setIdentityNotFoundByContact] = useState<Record<string, boolean>>({});
  const [postageRequiredByContact, setPostageRequiredByContact] = useState<Record<string, number>>({});
  const [refreshingContactId, setRefreshingContactId] = useState<number | null>(null);
  const [profileOverlayAlias, setProfileOverlayAlias] = useState<string | null>(null);
  const [profileSummaryByAlias, setProfileSummaryByAlias] = useState<Record<string, ProfileSummaryDto | null>>({});
  const profileSummaryFetchRef = useRef<Set<string>>(new Set());
  const searchRequestSeqRef = useRef(0);

  const prefetchProfileSummary = useCallback(async (aliasName: string) => {
    const key = aliasName.trim().toLowerCase();
    if (!key || profileSummaryFetchRef.current.has(key)) {
      return;
    }
    profileSummaryFetchRef.current.add(key);
    try {
      const result = await socialService.getProfileSummary(aliasName);
      const summary = result.data;
      setProfileSummaryByAlias((prev) => ({ ...prev, [key]: summary }));
    } catch {
      setProfileSummaryByAlias((prev) => ({ ...prev, [key]: null }));
    }
  }, []);

  const getProfileActionTooltip = (aliasName: string): string => {
    const key = aliasName.trim().toLowerCase();
    if (!key || !(key in profileSummaryByAlias)) {
      return 'View profile';
    }
    const summary = profileSummaryByAlias[key];
    if (summary?.canViewProfile) {
      return 'View profile';
    }
    return getProfileViewDeniedMessage(aliasName, summary);
  };

  // Form state for add from search
  const [addContactForm, setAddContactForm] = useState({
    displayName: '',
    nickname: '',
  });

  // Form state for manual add/edit
  const [formData, setFormData] = useState({
    displayName: '',
    address: '',
    deliveryRequired: '0',
    nickname: '',
  });

  useEffect(() => {
    loadContacts();
    loadDeliverySettings();
    loadIdentityDisclosurePreferences();
  }, []);

  useEffect(() => {
    if (!user?.username) return;

    const hydrateApprovedShields = async () => {
      try {
        const requests = await identityDisclosureService.listMyRequests();
        const next: Record<string, boolean> = {};
        requests.forEach((request) => {
          if ((request.status || '').toUpperCase() === 'APPROVED' && request.evidence) {
            next[request.recipientUsername.toLowerCase()] = true;
          }
        });
        if (Object.keys(next).length) {
          setApprovedShieldByUsername((prev) => ({ ...prev, ...next }));
        }
      } catch {
        // Non-blocking; shield click still resolves on demand.
      }
    };

    void hydrateApprovedShields();
  }, [user?.username]);

  useEffect(() => {
    if (!user?.username) return;

    const hydrateFromLocalStore = () => {
      const local = readContactLocalStore(user.username!);
      if (!local) return;
      setContacts(local.contacts);
      setContactLookupByAddress(getLocalLookupMap(user.username!));
      setIdentityNotFoundByContact(getLocalIdentityNotFoundMap(user.username!));
      setPostageRequiredByContact(getLocalOutboundPostageMap(user.username!));
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        hydrateFromLocalStore();
      }
    };

    window.addEventListener('focus', hydrateFromLocalStore);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', hydrateFromLocalStore);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user?.username]);

  const applyLocalStoreToState = useCallback((ownerUsername: string) => {
    const local = readContactLocalStore(ownerUsername);
    if (!local?.contacts?.length) return false;
    setContacts(local.contacts);
    seedContactIdentityCache(ownerUsername, local.contacts);
    setContactLookupByAddress(getLocalLookupMap(ownerUsername));
    setIdentityNotFoundByContact(getLocalIdentityNotFoundMap(ownerUsername));
    setPostageRequiredByContact(getLocalOutboundPostageMap(ownerUsername));
    return true;
  }, []);

  const syncContactsFromApi = async (forceRefresh = false) => {
    if (!user?.username) return;

    if (!forceRefresh && applyLocalStoreToState(user.username)) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await api.get<Contact[]>('/api/contacts');
      setContacts(data);
      mergeContactsIntoLocalStore(user.username, data);
      seedContactIdentityCache(user.username, data);
      setContactLookupByAddress(getLocalLookupMap(user.username));
      setIdentityNotFoundByContact(getLocalIdentityNotFoundMap(user.username));
      setPostageRequiredByContact(getLocalOutboundPostageMap(user.username));

    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const loadContacts = async () => {
    await syncContactsFromApi(false);
  };

  const refreshContactsFromApi = async () => {
    await syncContactsFromApi(true);
  };

  const syncOutboundPostageFromApi = useCallback(() => {
    if (!user?.username) return;
    setPostageRequiredByContact(getLocalOutboundPostageMap(user.username));
  }, [user?.username]);

  const applyContactPostageToState = useCallback((
    ownerUsername: string,
    contact: Contact,
    outboundRate: number
  ) => {
    const alias = extractAlias(contact.address).toLowerCase();
    if (alias) {
      setPostageRequiredByContact((prev) => ({ ...prev, [alias]: outboundRate }));
      setContacts((prev) => {
        const without = prev.filter((c) => extractAlias(c.address).toLowerCase() !== alias);
        return [...without, contact];
      });
    }
    setContactLookupByAddress(getLocalLookupMap(ownerUsername));
    setIdentityNotFoundByContact(getLocalIdentityNotFoundMap(ownerUsername));
    syncOutboundPostageFromApi();
  }, [syncOutboundPostageFromApi]);

  const refreshContactFromApiForRow = async (contact: Contact) => {
    if (!user?.username) return;
    const alias = extractAlias(contact.address);
    if (!alias) return;

    setRefreshingContactId(contact.contactID);
    try {
      const { lookup } = await refreshContactFromApi(user.username, alias);
      setContactLookupByAddress(getLocalLookupMap(user.username));
      setIdentityNotFoundByContact(getLocalIdentityNotFoundMap(user.username));
      syncOutboundPostageFromApi();

      if (lookup.status === 'not_found') {
        setSnackbar({
          open: true,
          message: `Identity not found for ${alias} — this address may no longer be valid. You can remove it from your address book.`,
          severity: 'error',
        });
      } else if (lookup.status === 'error') {
        setSnackbar({
          open: true,
          message: `Postage updated for ${alias}, but profile lookup failed. Try again later.`,
          severity: 'success',
        });
      } else {
        setSnackbar({
          open: true,
          message: `Contact info updated for ${alias}`,
          severity: 'success',
        });
      }
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.error || err?.message || `Failed to refresh ${alias}`,
        severity: 'error',
      });
    } finally {
      setRefreshingContactId(null);
    }
  };

  const loadDeliverySettings = async () => {
    try {
      const data = await api.get<UserDeliverySettings>('/api/contacts/settings');
      setDeliverySettings(data);
    } catch (err: any) {
      console.error('Failed to load delivery settings:', err);
    }
  };

  const loadIdentityDisclosurePreferences = async () => {
    try {
      const pref = await identityDisclosureService.getPreferences();
      setAutoApproveAllRequests(Boolean(pref.autoApproveAllRequests));
    } catch (err) {
      setAutoApproveAllRequests(true);
      console.error('Failed to load identity disclosure preferences:', err);
    }
  };

  const handleAutoApproveToggle = async (enabled: boolean) => {
    const previous = autoApproveAllRequests;
    setAutoApproveAllRequests(enabled);
    setAutoApproveSaving(true);
    try {
      const updated = await identityDisclosureService.updatePreferences({ autoApproveAllRequests: enabled });
      setAutoApproveAllRequests(Boolean(updated.autoApproveAllRequests));
      setSnackbar({
        open: true,
        message: enabled
          ? 'Auto-approve enabled: future ID verification requests auto-approve.'
          : 'Auto-approve disabled: each ID verification request now requires approval.',
        severity: 'success'
      });
    } catch (err: any) {
      setAutoApproveAllRequests(previous);
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || 'Failed to update ID disclosure auto-approve setting',
        severity: 'error'
      });
    } finally {
      setAutoApproveSaving(false);
    }
  };

  const saveDeliverySettings = async () => {
    setSettingsLoading(true);
    try {
      const data = await api.put<UserDeliverySettings>('/api/contacts/settings', {
        defaultDeliveryFeeRequired: deliverySettings.defaultDeliveryFeeRequired,
        defaultMaxPayAmount: deliverySettings.defaultMaxPayAmount,
        defaultDaysUntilMailExpires: deliverySettings.defaultDaysUntilMailExpires,
        maxDailyLimit: deliverySettings.maxDailyLimit,
        isUnlisted: deliverySettings.isUnlisted,
        doNotForwardSMTP: deliverySettings.doNotForwardSMTP,
        sendNewClientRequiredMessage: deliverySettings.sendNewClientRequiredMessage,
      });
      setDeliverySettings(data);
      setSettingsEdited(false);
      setSnackbar({ open: true, message: 'Delivery settings saved', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: 'Failed to save settings', severity: 'error' });
    } finally {
      setSettingsLoading(false);
    }
  };

  const resetSearchDialogState = useCallback(() => {
    setSelectedUser(null);
    setSearchTerm('');
    setSubmittedSearchTerm('');
    setSearchCountry('');
    setSearchState('');
    setSearchCity('');
    setSearchResults([]);
    setSearchStatus(null);
    setSearchMessage(null);
    setSearchRefinement(undefined);
  }, []);

  const handleSearchUsers = useCallback(async () => {
    const term = searchTerm.trim();
    if (!meetsContactSearchMinimum(term)) {
      setSearchResults([]);
      setSearchStatus('query_too_short');
      setSearchMessage(getContactSearchValidationMessage(term));
      setSearchRefinement(undefined);
      return;
    }

    const requestSeq = ++searchRequestSeqRef.current;
    setSearchLoading(true);
    setSubmittedSearchTerm(term);
    try {
      const data = await api.get<ContactSearchResponse>('/api/contacts/search', {
        params: {
          q: term,
          ...(searchCountry.trim() ? { country: searchCountry.trim() } : {}),
          ...(searchState.trim() ? { state: searchState.trim() } : {}),
          ...(searchCity.trim() ? { city: searchCity.trim() } : {}),
        },
      });
      if (requestSeq !== searchRequestSeqRef.current) {
        return;
      }
      if (isContactSearchResponse(data)) {
        setSearchStatus(data.status);
        setSearchMessage(data.message ?? null);
        setSearchRefinement(data.refinement);
        setSearchResults(data.status === 'ok' ? data.results : []);
      } else {
        setSearchStatus('ok');
        setSearchMessage(null);
        setSearchRefinement(undefined);
        setSearchResults(Array.isArray(data) ? data as UserSearchResult[] : []);
      }
    } catch (err: any) {
      console.error('Search failed:', err);
      if (requestSeq === searchRequestSeqRef.current) {
        setSearchResults([]);
        setSearchStatus(null);
        setSearchMessage(err?.response?.status === 429
          ? 'Too many searches. Please wait a moment and try again.'
          : 'Search failed. Please try again.');
        setSearchRefinement(undefined);
      }
    } finally {
      if (requestSeq === searchRequestSeqRef.current) {
        setSearchLoading(false);
      }
    }
  }, [searchTerm, searchCountry, searchState, searchCity]);

  const handleSearchKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      void handleSearchUsers();
    }
  };

  const handleSelectUser = (user: UserSearchResult) => {
    setSelectedUser(user);
    setAddContactForm({
      displayName: (user.fullName || user.username).trim(),
      nickname: '',
    });
  };

  const handleAddFromSearch = async () => {
    if (!selectedUser || !user?.username) return;
    const canonicalDisplayName = (selectedUser.fullName || selectedUser.username).trim();
    const nickname = addContactForm.nickname.trim();
    const address = `${selectedUser.username.trim()}@${getEmailDomainSync()}`;

    try {
      const created = await api.post<Contact & { contactId?: number }>('/api/contacts/add-from-search', {
        username: selectedUser.username,
        displayName: canonicalDisplayName || selectedUser.username,
        nickname: nickname || null,
      });

      const contactId = resolveContactId(created);
      const contactRecord: Contact = {
        ...created,
        contactID: contactId ?? created.contactID ?? 0,
        nickname: nickname || created.nickname,
        address: created.address || address,
      };

      let outboundRate = 0;
      try {
        const seeded = await seedNewContactAfterAdd(
          user.username,
          contactRecord,
          selectedUser.username
        );
        outboundRate = seeded.outboundRate;
        applyContactPostageToState(user.username, seeded.contact, outboundRate);
      } catch (postageErr) {
        console.warn('Outbound postage lookup failed after add:', postageErr);
        if (contactId) {
          upsertContactInLocalStore(user.username, contactRecord);
        } else {
          await refreshContactsFromApi();
        }
        applyLocalStoreToState(user.username);
      }

      setSnackbar({ open: true, message: 'Contact added successfully', severity: 'success' });
      setSearchDialogOpen(false);
      resetSearchDialogState();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to add contact', severity: 'error' });
    }
  };

  const handleOpenDialog = async (contact?: Contact) => {
    if (contact) {
      setEditingContact(contact);
      setFormData({
        displayName: contact.displayName,
        address: contact.address?.split('@')[0]?.trim() || contact.address,
        deliveryRequired: String(contact.deliveryRequired ?? 0),
        nickname: contact.nickname || '',
      });
    } else {
      setEditingContact(null);
      setFormData({
        displayName: '',
        address: '',
        deliveryRequired: deliverySettings.defaultDeliveryFeeRequired.toString(),
        nickname: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingContact(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    const persistedDisplayName = editingContact
      ? (editingContact.displayName || '').trim()
      : formData.displayName.trim();
    if (!persistedDisplayName) {
      setSnackbar({ open: true, message: 'Display name is required', severity: 'error' });
      return;
    }
    const qixitId = formData.address.trim();
    if (!qixitId) {
      setSnackbar({ open: true, message: 'Qixit ID is required', severity: 'error' });
      return;
    }

    const normalizedAddress = qixitId.includes('@')
      ? qixitId
      : `${qixitId}@${getEmailDomainSync()}`;

    const contactData = {
      displayName: persistedDisplayName,
      firstName: null,
      middleName: null,
      lastName: null,
      nickname: formData.nickname.trim() || null,
      address: normalizedAddress,
      deliveryRequired: parseFloat(formData.deliveryRequired) || 0,
      maxPay: 0,
    };

    try {
      if (editingContact) {
        const updated = await api.put<Contact>(`/api/contacts/${editingContact.contactID}`, contactData);
        if (user?.username) {
          setContactInboundRate(user.username, normalizedAddress, contactData.deliveryRequired);
          setContactNickname(user.username, normalizedAddress, formData.nickname.trim(), persistedDisplayName);
          upsertContactInLocalStore(user.username, updated ?? { ...editingContact, ...contactData, contactID: editingContact.contactID });
          applyLocalStoreToState(user.username);
        }
        setSnackbar({ open: true, message: 'Contact updated successfully', severity: 'success' });
      } else {
        const created = await api.post<Contact & { contactId?: number }>('/api/contacts', contactData);
        if (user?.username && created) {
          const contactId = resolveContactId(created);
          const contactRecord: Contact = {
            ...created,
            contactID: contactId ?? created.contactID ?? 0,
            ...contactData,
          };
          const alias = extractAlias(normalizedAddress);
          try {
            const seeded = await seedNewContactAfterAdd(user.username, contactRecord, alias);
            applyContactPostageToState(user.username, seeded.contact, seeded.outboundRate);
          } catch {
            upsertContactInLocalStore(user.username, contactRecord);
            applyLocalStoreToState(user.username);
          }
        }
        setSnackbar({ open: true, message: 'Contact created successfully', severity: 'success' });
      }
      handleCloseDialog();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Failed to save contact', severity: 'error' });
    }
  };

  const handleDelete = async (contactId: number, confirmMessage?: string) => {
    const message = confirmMessage ?? 'Are you sure you want to delete this contact?';
    if (!window.confirm(message)) return;

    try {
      await api.delete(`/api/contacts/${contactId}`);
      if (user?.username) {
        removeContactFromLocalStore(user.username, contactId);
        applyLocalStoreToState(user.username);
      }
      setSnackbar({ open: true, message: 'Contact deleted successfully', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Failed to delete contact', severity: 'error' });
    }
  };

  const handleDeleteDeadContact = (contact: Contact) => {
    const alias = getContactUsername(contact) || contact.displayName || 'this contact';
    void handleDelete(
      contact.contactID,
      `"${alias}" is no longer a valid Qixit identity. Remove it from your address book?`
    );
  };

  const handleOpenFraudDialog = (
    contact: Contact,
    preset?: Partial<{ requestIdentityPhotos: boolean; requestMailingAddress: boolean }>
  ) => {
    setFraudTarget(contact);
    setFraudSelfConfirm(false);
    setFraudForm({
      requestIdentityPhotos: preset?.requestIdentityPhotos ?? true,
      requestMailingAddress: preset?.requestMailingAddress ?? false,
    });
    setFraudDialogOpen(true);
  };

  const showApprovedShieldSnapshot = (
    contact: Contact,
    snapshot: IdentityDisclosureShieldSnapshot
  ) => {
    const contactKey = getContactUsername(contact).toLowerCase();
    setShieldTarget(contact);
    setShieldSnapshot(snapshot);
    setShieldError(null);
    setShieldRequestMailingAddress(false);
    setShieldDialogOpen(true);
    if (contactKey) {
      setApprovedShieldByUsername((prev) => ({ ...prev, [contactKey]: true }));
    }
  };

  const resolveShieldActionForContact = async (contact: Contact) => {
    const recipientUsername = getContactUsername(contact);
    if (!recipientUsername) {
      setSnackbar({ open: true, message: 'Could not determine contact username', severity: 'error' });
      return;
    }

    const contactKey = recipientUsername.toLowerCase();
    setShieldCheckingUsername(contactKey);
    try {
      const snapshot = await resolveApprovedShieldSnapshot(recipientUsername);
      if (snapshot) {
        showApprovedShieldSnapshot(contact, snapshot);
        return;
      }

      setApprovedShieldByUsername((prev) => ({ ...prev, [contactKey]: false }));
      handleOpenFraudDialog(contact);
    } catch (err: any) {
      setApprovedShieldByUsername((prev) => ({ ...prev, [contactKey]: false }));
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || 'Could not check verification snapshot. Try submitting a new request.',
        severity: 'error',
      });
      handleOpenFraudDialog(contact);
    } finally {
      setShieldCheckingUsername(null);
    }
  };

  const handleShieldClick = (contact: Contact) => {
    void resolveShieldActionForContact(contact);
  };

  const getContactUsername = (contact?: Contact | null): string =>
    contact?.address?.split('@')[0]?.trim() || '';

  const getPostageRequiredByContact = (contact: Contact): number | null => {
    const contactUsername = getContactUsername(contact).toLowerCase();
    if (!contactUsername || !user?.username) return null;

    const localRate = getLocalOutboundPostage(user.username, contact.address);
    if (localRate !== undefined) return localRate;

    if (Object.prototype.hasOwnProperty.call(postageRequiredByContact, contactUsername)) {
      return postageRequiredByContact[contactUsername];
    }

    return deliverySettings.defaultMaxPayAmount ?? 0;
  };

  const isSelfFraudTarget = (() => {
    const targetUsername = getContactUsername(fraudTarget).toLowerCase();
    const currentUsername = (user?.username || '').trim().toLowerCase();
    return !!targetUsername && !!currentUsername && targetUsername === currentUsername;
  })();

  const resolveDisplayNameForContact = (contact: Contact): string => {
    const contactUsername = contact.address?.split('@')[0]?.trim().toLowerCase() || '';
    const contactInfo = contactLookupByAddress[contactUsername];
    const canonicalDisplayName = (contactInfo?.fullName || '').trim();
    const storedDisplayName = (contact.displayName || '').trim();
    const shouldUseCanonicalDisplay =
      canonicalDisplayName.length > 0 &&
      storedDisplayName.length > 0 &&
      storedDisplayName.toLowerCase() === contactUsername &&
      canonicalDisplayName.toLowerCase() !== storedDisplayName.toLowerCase();

    return shouldUseCanonicalDisplay ? canonicalDisplayName : contact.displayName;
  };

  const handleComposeToContact = (contact: Contact) => {
    const params = new URLSearchParams();
    params.set('compose', '1');
    params.set('to', contact.address);
    params.set('name', resolveDisplayNameForContact(contact) || contact.address);
    params.set('required', String(getPostageRequiredByContact(contact) ?? 0));
    navigate(`/messages?${params.toString()}`);
  };

  const handleSubmitFraudRequest = async () => {
    if (!fraudTarget) return;
    const recipientUsername = fraudTarget.address?.split('@')[0]?.trim();
    if (!recipientUsername) {
      setSnackbar({ open: true, message: 'Could not determine contact username', severity: 'error' });
      return;
    }

    if (!fraudForm.requestIdentityPhotos && !fraudForm.requestMailingAddress) {
      setSnackbar({ open: true, message: 'Select at least one verification item', severity: 'error' });
      return;
    }

    setFraudSubmitting(true);
    const paidContact = fraudTarget;
    const isSelfTarget = (() => {
      const targetUsername = getContactUsername(paidContact).toLowerCase();
      const currentUsername = (user?.username || '').trim().toLowerCase();
      return !!targetUsername && !!currentUsername && targetUsername === currentUsername;
    })();
    try {
      const response = await identityDisclosureService.createRequest({
        recipientUsername,
        requestIdentityPhotos: fraudForm.requestIdentityPhotos,
        requestMailingAddress: fraudForm.requestMailingAddress,
      });
      const contactKey = recipientUsername.toLowerCase();
      const isApproved = (response.status || '').toUpperCase() === 'APPROVED';
      setFraudDialogOpen(false);
      setFraudTarget(null);

      if (isApproved && paidContact) {
        const snapshot = await resolveApprovedShieldSnapshot(recipientUsername, response.requestId);
        if (snapshot) {
          showApprovedShieldSnapshot(paidContact, snapshot);
          setSnackbar({
            open: true,
            message: isSelfTarget
              ? 'Self-request approved. Verification details are shown below. $2.00 non-refundable process fee charged.'
              : 'Request approved. Verification photos are now available below. $2.00 process fee charged.',
            severity: 'success',
          });
          return;
        }

        setSnackbar({
          open: true,
          message: 'Payment succeeded but verification details are still loading. Click the shield again in a moment.',
          severity: 'warning',
        });
        return;
      }

      setApprovedShieldByUsername((prev) => ({ ...prev, [contactKey]: false }));
      setSnackbar({
        open: true,
        message: isSelfTarget
          ? 'Self-request submitted as preview. $2.00 non-refundable process fee charged.'
          : 'ID verification request submitted. $2.00 process fee charged. The recipient will be notified.',
        severity: 'success'
      });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || err?.message || 'Failed to submit ID verification request',
        severity: 'error'
      });
    } finally {
      setFraudSubmitting(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Address Book</Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SearchIcon />}
            onClick={() => setSearchDialogOpen(true)}
          >
            Find Contact
          </Button>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Manually
          </Button>
        </Box>
      </Box>

      {/* Default Delivery Settings Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <SettingsIcon color="primary" />
            <Typography variant="h6">Default Delivery Settings</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" mb={2}>
            These defaults are applied when adding new contacts. They can be overridden per contact.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
            <Box sx={{ flex: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Default Postage Required from Unknown Senders"
                type="number"
                value={deliverySettings.defaultDeliveryFeeRequired}
                onChange={(e) => {
                  setDeliverySettings(prev => ({ 
                    ...prev, 
                    defaultDeliveryFeeRequired: parseFloat(e.target.value) || 0 
                  }));
                  setSettingsEdited(true);
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                inputProps={{ min: 0, step: 0.01 }}
                helperText="Fee required to receive messages from unknown senders"
              />
            </Box>
            <Box sx={{ flex: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Alert Me If Postage Is Over"
                type="number"
                value={deliverySettings.defaultMaxPayAmount}
                onChange={(e) => {
                  setDeliverySettings(prev => ({ 
                    ...prev, 
                    defaultMaxPayAmount: parseFloat(e.target.value) || 0 
                  }));
                  setSettingsEdited(true);
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                inputProps={{ min: 0, step: 0.01 }}
                helperText="Warn before send when recipient postage exceeds this amount"
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={settingsLoading ? <CircularProgress size={20} /> : <SaveIcon />}
                onClick={saveDeliverySettings}
                disabled={!settingsEdited || settingsLoading}
              >
                Save
              </Button>
            </Box>
          </Stack>

          {/* Advanced Settings Expandable Section */}
          <Box sx={{ mt: 2 }}>
            <Button
              size="small"
              onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
              endIcon={showAdvancedSettings ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{ textTransform: 'none' }}
            >
              {showAdvancedSettings ? 'Hide' : 'Show'} Advanced Settings
            </Button>
            <Collapse in={showAdvancedSettings}>
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={2}>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Default Days Until Mail Expires"
                      type="number"
                      value={deliverySettings.defaultDaysUntilMailExpires}
                      onChange={(e) => {
                        setDeliverySettings(prev => ({ 
                          ...prev, 
                          defaultDaysUntilMailExpires: parseInt(e.target.value) || 90 
                        }));
                        setSettingsEdited(true);
                      }}
                      inputProps={{ min: 1, max: 365 }}
                      helperText="Number of days until unread messages expire"
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Maximum Daily Transaction Limit"
                      type="number"
                      value={deliverySettings.maxDailyLimit}
                      onChange={(e) => {
                        setDeliverySettings(prev => ({ 
                          ...prev, 
                          maxDailyLimit: parseFloat(e.target.value) || 0 
                        }));
                        setSettingsEdited(true);
                      }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                      inputProps={{ min: 0, step: 10 }}
                      helperText="Maximum daily transaction limit"
                    />
                  </Box>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={deliverySettings.isUnlisted}
                        onChange={(e) => {
                          setDeliverySettings(prev => ({ 
                            ...prev, 
                            isUnlisted: e.target.checked 
                          }));
                          setSettingsEdited(true);
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">Hide from User Search</Typography>
                        <Typography variant="caption" color="text.secondary">
                          When enabled, your account won't appear in search results
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: 'flex-start', ml: 0 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={deliverySettings.doNotForwardSMTP}
                        onChange={(e) => {
                          setDeliverySettings(prev => ({ 
                            ...prev, 
                            doNotForwardSMTP: e.target.checked 
                          }));
                          setSettingsEdited(true);
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">Do Not Forward via SMTP</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Disable forwarding messages to external email
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: 'flex-start', ml: 0 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={deliverySettings.sendNewClientRequiredMessage}
                        onChange={(e) => {
                          setDeliverySettings(prev => ({ 
                            ...prev, 
                            sendNewClientRequiredMessage: e.target.checked 
                          }));
                          setSettingsEdited(true);
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">Send New Client Required Message</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Notify SMTP senders to use Qixit client
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: 'flex-start', ml: 0 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={autoApproveAllRequests}
                        disabled={autoApproveSaving}
                        onChange={(e) => void handleAutoApproveToggle(e.target.checked)}
                      />
                    }
                    label={(
                      <Box>
                        <Typography variant="body2">Auto-Approve ID Verification Requests</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Automatically approve future paid identity-photo disclosure requests only. Mailing-address requests always require manual approval.
                        </Typography>
                      </Box>
                    )}
                    sx={{ alignItems: 'flex-start', ml: 0 }}
                  />
                </Stack>
              </Box>
            </Collapse>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Contacts Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Display Name</TableCell>
                <TableCell>Qixit ID</TableCell>
                <TableCell align="right">Postage Required By Contact</TableCell>
                <TableCell align="right">Postage Required From Contact</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : contacts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="text.secondary" sx={{ py: 3 }}>
                      No contacts yet. Use "Find Contact" to search for Qixit users to add!
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                contacts.map((contact) => (
                  <TableRow
                    key={contact.contactID}
                    hover
                    onDoubleClick={() => handleComposeToContact(contact)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                        {(() => {
                          const contactUsername = contact.address?.split('@')[0]?.trim().toLowerCase() || '';
                          const contactInfo = contactLookupByAddress[contactUsername];
                          const canonicalDisplayName = (contactInfo?.fullName || '').trim();
                          const storedDisplayName = (contact.displayName || '').trim();
                          const shouldUseCanonicalDisplay =
                            canonicalDisplayName.length > 0 &&
                            storedDisplayName.length > 0 &&
                            storedDisplayName.toLowerCase() === contactUsername &&
                            canonicalDisplayName.toLowerCase() !== storedDisplayName.toLowerCase();
                          const displayNameToShow = shouldUseCanonicalDisplay ? canonicalDisplayName : contact.displayName;

                          return (
                            <>
                        <Typography variant="body2" fontWeight="bold">
                              {displayNameToShow}
                        </Typography>
                              {identityNotFoundByContact[contactUsername] && (
                                <>
                                  <Tooltip title="This address is no longer a valid Qixit identity (deleted alias or removed user).">
                                    <Chip
                                      label="Identity not found"
                                      size="small"
                                      color="error"
                                      variant="outlined"
                                    />
                                  </Tooltip>
                                  <Button
                                    size="small"
                                    color="error"
                                    variant="text"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteDeadContact(contact);
                                    }}
                                    sx={{ minWidth: 0, py: 0, textTransform: 'none' }}
                                  >
                                    Remove
                                  </Button>
                                </>
                              )}
                              {(() => {
                                if (!contactInfo) return null;
                                return (
                                  <>
                              {isTraceableChip(contactInfo) && (
                                <Chip label="Traceable" size="small" color="success" variant="outlined" />
                              )}
                              {isIdVerifiedNameChip(contactInfo) && (
                                <Tooltip title={getIdVerifiedChipTooltip(contactInfo)} arrow>
                                  <Chip
                                    label="ID Verified Name"
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                    icon={<IdCardIcon sx={{ color: 'success.main' }} />}
                                  />
                                </Tooltip>
                              )}
                              <VerificationBadge
                                isEmailVerified={contactInfo.isEmailVerified}
                                isPhoneVerified={contactInfo.isPhoneVerified}
                                isIdVerified={contactInfo.isIdVerified}
                                isAddressVerified={contactInfo.isAddressVerified}
                                firstIdVerifiedDate={contactInfo.firstIdVerifiedDate}
                                latestIdVerifiedDate={contactInfo.latestIdVerifiedDate}
                                joinedQixitDate={contactInfo.joinedQixitDate}
                                size="small"
                              />
                                  </>
                                );
                              })()}
                        {(contact.nickname || '').trim() && (
                          <Typography variant="caption" color="text.secondary">
                            Nickname: {contact.nickname}
                          </Typography>
                        )}
                            </>
                          );
                        })()}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={<MailIcon />}
                        label={getContactUsername(contact) || '—'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title="Postage you must pay to this contact">
                          {(() => {
                            const requiredPostage = getPostageRequiredByContact(contact);
                            return (
                              <Chip
                                label={`$${(requiredPostage ?? 0).toFixed(2)}`}
                                size="small"
                                color={(requiredPostage ?? 0) > 0 ? 'warning' : 'default'}
                              />
                            );
                          })()}
                        </Tooltip>
                        <Tooltip title="Refresh postage, verification badges, and display name">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => void refreshContactFromApiForRow(contact)}
                              disabled={refreshingContactId === contact.contactID}
                              aria-label={`Refresh contact info for ${getContactUsername(contact)}`}
                            >
                              {refreshingContactId === contact.contactID ? (
                                <CircularProgress size={16} />
                              ) : (
                                <SyncIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Postage you require for messages from this contact">
                        <Chip
                          label={`$${contact.deliveryRequired.toFixed(2)}`}
                          size="small"
                          color={contact.deliveryRequired > 0 ? "info" : "default"}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      {(() => {
                        const alias = getContactUsername(contact);
                        const key = alias.trim().toLowerCase();
                        const summary = key ? profileSummaryByAlias[key] : undefined;
                        const profileMuted = summary != null && !summary.canViewProfile;
                        return (
                      <Tooltip title={getProfileActionTooltip(alias)}>
                        <span>
                          <IconButton
                            size="small"
                            onMouseEnter={() => void prefetchProfileSummary(alias)}
                            onFocus={() => void prefetchProfileSummary(alias)}
                            onClick={() => setProfileOverlayAlias(alias)}
                            color={profileMuted ? 'default' : 'primary'}
                            sx={profileMuted ? { opacity: 0.55 } : undefined}
                            aria-label={`View profile for ${alias}`}
                          >
                            <PersonIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                        );
                      })()}
                      <IconButton
                        size="small"
                        onClick={() => handleComposeToContact(contact)}
                        color="success"
                        title="Compose message"
                      >
                        <SendIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleOpenDialog(contact)} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => void handleShieldClick(contact)}
                        disabled={shieldCheckingUsername === getContactUsername(contact).toLowerCase()}
                        color={(() => {
                          const contactUsername = getContactUsername(contact).toLowerCase();
                          if (approvedShieldByUsername[contactUsername]) {
                            return 'success';
                          }
                          const contactInfo = contactLookupByAddress[contactUsername];
                          const isTraceableOrIdVerifiedName = Boolean(contactInfo) &&
                            (isTraceableChip(contactInfo) || isIdVerifiedNameChip(contactInfo));
                          return isTraceableOrIdVerifiedName ? 'primary' : 'default';
                        })()}
                        title={(() => {
                          const contactUsername = getContactUsername(contact).toLowerCase();
                          if (approvedShieldByUsername[contactUsername]) {
                            return 'Approved purchased snapshot available';
                          }
                          const contactInfo = contactLookupByAddress[contactUsername];
                          const isTraceableOrIdVerifiedName = Boolean(contactInfo) &&
                            (isTraceableChip(contactInfo) || isIdVerifiedNameChip(contactInfo));
                          return isTraceableOrIdVerifiedName
                            ? 'Traceable / ID-verified contact: pay to request verification details'
                            : 'Untraceable contact: pay to send ID verification request';
                        })()}
                      >
                        {shieldCheckingUsername === getContactUsername(contact).toLowerCase()
                          ? <CircularProgress size={18} />
                          : <SecurityIcon />}
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(contact.contactID)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Search Dialog */}
      <Dialog 
        open={searchDialogOpen} 
        onClose={() => {
          setSearchDialogOpen(false);
          resetSearchDialogState();
        }} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <PersonAddIcon color="primary" />
            Find and Add Contact
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Search by username or display name. Type at least 4 letters, then press Enter (use a space for first and last name, e.g. John Sm).
          </Typography>
          
          <Box display="flex" gap={1} mb={2}>
            <TextField
              fullWidth
              placeholder="Username or name (min. 4 letters, press Enter)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              InputProps={{
                startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                endAdornment: searchLoading ? <CircularProgress size={20} /> : null,
              }}
            />
            <Button
              variant="contained"
              onClick={() => void handleSearchUsers()}
              disabled={searchLoading}
              sx={{ whiteSpace: 'nowrap', minWidth: '6rem' }}
            >
              Search
            </Button>
          </Box>

          {(searchRefinement?.countryRequired || searchRefinement?.stateRequired || searchRefinement?.cityRequired || searchCountry || searchState || searchCity) && (
            <Stack spacing={2} sx={{ mb: 2 }}>
              {(searchRefinement?.countryRequired || searchCountry) && (
                <TextField
                  fullWidth
                  label="Country"
                  placeholder="e.g. United States"
                  value={searchCountry}
                  onChange={(e) => setSearchCountry(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  required={Boolean(searchRefinement?.countryRequired)}
                />
              )}
              {(searchRefinement?.stateRequired || searchState) && (
                <TextField
                  fullWidth
                  label="State / Province"
                  placeholder="e.g. WA"
                  value={searchState}
                  onChange={(e) => setSearchState(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  required={Boolean(searchRefinement?.stateRequired)}
                />
              )}
              {(searchRefinement?.cityRequired || searchCity) && (
                <TextField
                  fullWidth
                  label="City"
                  placeholder="e.g. Seattle"
                  value={searchCity}
                  onChange={(e) => setSearchCity(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  required={Boolean(searchRefinement?.cityRequired)}
                />
              )}
            </Stack>
          )}

          {searchMessage && searchStatus !== 'ok' && (
            <Alert
              severity={searchStatus === 'refinement_required' ? 'warning' : searchStatus === 'too_many' ? 'error' : 'info'}
              sx={{ mb: 2 }}
            >
              {searchMessage}
            </Alert>
          )}

          {searchResults.length > 0 && !selectedUser && (
            <Paper variant="outlined" sx={{ maxHeight: 360, overflow: 'auto', mb: 2 }}>
              <List dense disablePadding>
                {searchResults.map((user) => {
                  const locationLine = formatContactSearchLocation(user);
                  return (
                  <ListItem
                    key={user.username}
                    disablePadding
                    divider
                    secondaryAction={
                      <Tooltip title={getSearchResultProfileTooltip(user)}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<PersonIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            setProfileOverlayAlias(user.username);
                          }}
                          sx={{ mr: 1, whiteSpace: 'nowrap' }}
                        >
                          View profile
                        </Button>
                      </Tooltip>
                    }
                  >
                    <ListItemButton onClick={() => handleSelectUser(user)} sx={{ pr: '10rem' }}>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            <Typography fontWeight="bold">{(user.fullName || user.username).trim()}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {`<${user.username}>`}
                            </Typography>
                            {isIdVerifiedNameChip(user) && (
                              <Tooltip title={getIdVerifiedChipTooltip(user)} arrow>
                                <Chip
                                  label="ID Verified Name"
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                  icon={<IdCardIcon sx={{ color: 'success.main' }} />}
                                />
                              </Tooltip>
                            )}
                            {user.isBusinessAccount && (
                              <Tooltip title="This login name belongs to a business account" arrow>
                                <Chip label="Business Account" size="small" color="warning" variant="outlined" />
                              </Tooltip>
                            )}
                            <VerificationBadge
                              isEmailVerified={user.isEmailVerified}
                              isPhoneVerified={user.isPhoneVerified}
                              isIdVerified={user.isIdVerified}
                              isAddressVerified={user.isAddressVerified}
                              firstIdVerifiedDate={user.firstIdVerifiedDate}
                              latestIdVerifiedDate={user.latestIdVerifiedDate}
                              joinedQixitDate={user.joinedQixitDate}
                              size="small"
                            />
                            <FinancialTrustBadge tier={user.externalPaymentTrustTier} size="small" />
                            {!user.isActive && (
                              <Chip label="Inactive" size="small" color="default" />
                            )}
                          </Box>
                        }
                        secondary={
                          locationLine ? (
                            <Typography variant="body2" color="text.secondary" component="span">
                              {locationLine}
                              {' (account location)'}
                            </Typography>
                          ) : undefined
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                  );
                })}
              </List>
            </Paper>
          )}

          {submittedSearchTerm && searchStatus === 'ok' && searchResults.length === 0 && !searchLoading && (
            <Alert severity="info" sx={{ mb: 2 }}>
              No users found matching "{submittedSearchTerm}"
            </Alert>
          )}

          {selectedUser && (
            <Card variant="outlined" sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Add {selectedUser.username} to Contacts
                </Typography>
                
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    label="Display name defined by the contact"
                    value={addContactForm.displayName}
                    disabled
                  />
                  <TextField
                    fullWidth
                    label="Nickname"
                    value={addContactForm.nickname}
                    onChange={(e) => setAddContactForm(prev => ({ ...prev, nickname: e.target.value }))}
                    helperText="Your private label; shown only in your message lists"
                  />
                </Stack>
              </CardContent>
            </Card>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setSearchDialogOpen(false);
            resetSearchDialogState();
          }}>
            Cancel
          </Button>
          {selectedUser && (
            <>
              <Button
                startIcon={<PersonIcon />}
                onClick={() => setProfileOverlayAlias(selectedUser.username)}
              >
                View Profile
              </Button>
              <Button onClick={() => setSelectedUser(null)}>
                Back to Search
              </Button>
              <Button 
                variant="contained" 
                startIcon={<PersonAddIcon />}
                onClick={handleAddFromSearch}
              >
                Add Contact
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={shieldDialogOpen} onClose={() => setShieldDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Verified Identity Snapshot</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {shieldError && (
              <Alert severity="error">{shieldError}</Alert>
            )}
            {shieldSnapshot && (
              <>
                <Alert severity="info">
                  This is a snapshot on the date your request was approved. Requesting an updated verification may, or may not, provide a newer update.
                </Alert>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={shieldRequestMailingAddress}
                      onChange={(e) => setShieldRequestMailingAddress(e.target.checked)}
                    />
                  )}
                  label="Request ID-confirmed mailing address in your next verification request ($2 system fee)."
                />
                <Typography variant="body2">
                  Recipient: <strong>{shieldTarget?.displayName}</strong> ({shieldTarget?.address})
                </Typography>
                <Typography variant="body2">
                  Approved: <strong>{new Date(shieldSnapshot.approvedAtUtc).toLocaleString()}</strong>
                </Typography>
                {shieldSnapshot.evidence.verifiedFullName && (
                  <Typography variant="body2">
                    Verified name: <strong>{shieldSnapshot.evidence.verifiedFullName}</strong>
                  </Typography>
                )}
                {(shieldSnapshot.evidence.idState || shieldSnapshot.evidence.idCountry) && (
                  <Typography variant="body2">
                    ID location: <strong>{[shieldSnapshot.evidence.idState, shieldSnapshot.evidence.idCountry].filter(Boolean).join(', ')}</strong>
                  </Typography>
                )}
                {shieldSnapshot.requestMailingAddress && shieldSnapshot.evidence.mailingAddressLine1 && (
                  <Typography variant="body2">
                    ID-confirmed mailing address:{' '}
                    <strong>
                      {formatMailingAddressForDisplay(shieldSnapshot.evidence)}
                    </strong>
                  </Typography>
                )}
                {shieldSnapshot.requestIdentityPhotos && shieldSnapshot.evidence.portraitImageUrl && (
                  <Box>
                    <Typography variant="subtitle2">ID portrait</Typography>
                    <Box component="img" src={shieldSnapshot.evidence.portraitImageUrl} alt="ID portrait" sx={{ mt: 1, maxWidth: 280, width: '100%', borderRadius: 1 }} />
                  </Box>
                )}
                {shieldSnapshot.requestIdentityPhotos && shieldSnapshot.evidence.livenessVideoUrl && (
                  <Box>
                    <Typography variant="subtitle2">Didit live capture</Typography>
                    <Box component="video" controls src={shieldSnapshot.evidence.livenessVideoUrl} sx={{ mt: 1, maxWidth: 320, width: '100%', borderRadius: 1 }} />
                  </Box>
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShieldDialogOpen(false)}>Close</Button>
          {shieldSnapshot && shieldTarget && (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                setShieldDialogOpen(false);
                handleOpenFraudDialog(
                  shieldTarget,
                  shieldRequestMailingAddress
                    ? { requestIdentityPhotos: false, requestMailingAddress: true }
                    : undefined
                );
              }}
            >
              Request New Verification ($2)
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={fraudDialogOpen} onClose={() => setFraudDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>ID Verification Request</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="warning">
              This request costs <strong>$2.00</strong>. The recipient is not required to respond.
            </Alert>
            {isSelfFraudTarget && (
              <Alert severity="info">
                You are requesting verification disclosure on your own account as a paid preview.
                The <strong>$2.00 fee is non-refundable</strong>.
              </Alert>
            )}
            <Typography variant="body2">
              Recipient: <strong>{fraudTarget?.displayName}</strong> ({fraudTarget?.address})
            </Typography>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={fraudForm.requestIdentityPhotos}
                  onChange={(e) => setFraudForm((prev) => ({ ...prev, requestIdentityPhotos: e.target.checked }))}
                />
              )}
              label="Request verified true name + ID portrait + Didit live capture"
            />
            <FormControlLabel
              control={(
                <Checkbox
                  checked={fraudForm.requestMailingAddress}
                  onChange={(e) => setFraudForm((prev) => ({ ...prev, requestMailingAddress: e.target.checked }))}
                />
              )}
              label="Request ID-confirmed mailing address"
            />
            <Typography variant="caption" color="text.secondary">
              The recipient may re-verify their ID first if they prefer a different photo to be shown.
            </Typography>
            {isSelfFraudTarget && (
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={fraudSelfConfirm}
                    onChange={(e) => setFraudSelfConfirm(e.target.checked)}
                  />
                )}
                label="I understand this is a self-request preview and the $2.00 fee is non-refundable."
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFraudDialogOpen(false)} disabled={fraudSubmitting}>Cancel</Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSubmitFraudRequest}
            disabled={fraudSubmitting || (isSelfFraudTarget && !fraudSelfConfirm)}
          >
            {fraudSubmitting ? 'Submitting...' : 'Pay $2 and Send Request'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Contact Dialog (Manual) */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingContact ? 'Edit Contact' : 'Add Contact Manually'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Alias display name"
              name="displayName"
              value={formData.displayName}
              onChange={handleInputChange}
              required
              disabled={Boolean(editingContact)}
              helperText={editingContact
                ? 'Set by the alias owner; shown on their outbound messages.'
                : 'Official name from the alias owner when available'}
            />
            <TextField
              fullWidth
              label="Qixit ID"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              required
              disabled={Boolean(editingContact)}
              placeholder="Beta01"
              helperText={editingContact
                ? 'Qixit ID cannot be changed after the contact is added.'
                : 'Unique alias (without @domain)'}
            />
            <TextField
              fullWidth
              label="Nickname"
              name="nickname"
              value={formData.nickname}
              onChange={handleInputChange}
              helperText="Your private label; shown only in your message lists"
            />

            <Box>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
                Payment Settings
              </Typography>
            </Box>

            <TextField
              fullWidth
              label="Postage Required From This Contact"
              name="deliveryRequired"
              type="number"
              value={formData.deliveryRequired}
              onChange={handleInputChange}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              inputProps={{ min: 0, step: 0.01 }}
              helperText="Fee you require to receive from this contact"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editingContact ? 'Save' : 'Add Contact'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Profile overlay (public or shared-with-contact visibility) */}
      <ProfileOverlayDialog
        open={Boolean(profileOverlayAlias)}
        aliasName={profileOverlayAlias ?? ''}
        onClose={() => setProfileOverlayAlias(null)}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AddressBook;
